import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { googleDriveIntegrationService } from '@/features/google-drive-integration/services/google-drive-integration.service';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/google-drive-oauth-callback')({
  component: GoogleDriveOAuthCallbackPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      code: (search.code as string) || null,
      error: (search.error as string) || null,
    };
  },
});

function GoogleDriveOAuthCallbackPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { code, error } = useSearch({ from: '/google-drive-oauth-callback' });
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const hasProcessedRef = useRef(false);

  const connectMutation = useMutation({
    mutationFn: (authCode: string) =>
      googleDriveIntegrationService.connectOAuth(authCode),
    onSuccess: () => {
      setStatus('success');
      toast({
        title: 'Integração conectada',
        description: 'Sua conta Google foi conectada ao Drive/Docs com sucesso!',
      });
      setTimeout(() => {
        navigate({ to: '/google-drive-config' });
      }, 2000);
    },
    onError: (error: any) => {
      setStatus('error');
      toast({
        title: 'Erro ao conectar',
        description:
          error?.response?.data?.message ||
          error.message ||
          'Não foi possível conectar a conta Google.',
        variant: 'destructive',
      });
      setTimeout(() => {
        navigate({ to: '/google-drive-config' });
      }, 3000);
    },
  });

  useEffect(() => {
    // Prevenir execução múltipla
    if (hasProcessedRef.current) {
      return;
    }

    if (error) {
      hasProcessedRef.current = true;
      setStatus('error');
      toast({
        title: 'Erro na autorização',
        description: 'A autorização foi negada ou ocorreu um erro.',
        variant: 'destructive',
      });
      setTimeout(() => {
        navigate({ to: '/google-drive-config' });
      }, 3000);
      return;
    }

    if (code && !connectMutation.isPending) {
      hasProcessedRef.current = true;
      connectMutation.mutate(code);
    }
  }, [code, error, navigate, toast, connectMutation]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          {status === 'loading' && (
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              <p className="text-muted-foreground">Conectando sua conta Google...</p>
            </div>
          )}
          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="text-green-500 text-4xl">✓</div>
              <p className="text-lg font-medium">Conta conectada com sucesso!</p>
              <p className="text-sm text-muted-foreground">Redirecionando...</p>
            </div>
          )}
          {status === 'error' && (
            <div className="text-center space-y-4">
              <div className="text-red-500 text-4xl">✗</div>
              <p className="text-lg font-medium">Erro ao conectar</p>
              <p className="text-sm text-muted-foreground">Redirecionando...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
